import { EM_DASH, api_error, fmt, fmt_count, fmt_signed, is_permission_error } from '../format';
import { day_label, stamp_label } from '../base_rate/logic';
import { IST_OFFSET_MS, ist_day } from '../shared/ist';

export { day_label, stamp_label };

// The seed is stored from 1 Jan 2000 (before any payable month); Ops read it as the system's
// opening, 1 Oct 2026.
export const SEED_EFFECTIVE_DATE = '2000-01-01';
export const SYSTEM_OPENING_DATE = '2026-10-01';
export const version_day_label = (d) =>
  day_label(d === SEED_EFFECTIVE_DATE ? SYSTEM_OPENING_DATE : d);

// rules_enums.json limits/ranges; contract.test.js pins them to the published values.
export const LIMITS = {
  note_min_len: 10,
  note_max_len: 1000,
  notice_min_len: 10,
  notice_max_len: 1000,
  no_notice_min_len: 10,
  no_notice_max_len: 500,
  preview_max_age_hours: 24,
  big_impact_rupees: 10000,
  big_impact_teachers: 50,
  approval_note_min_len: 10,
  approval_note_max_len: 1000,
  approval_queue_cap: 100
};

export const RANGES = {
  pay_pct_max: 100,
  penalty_pct_max: 200,
  incentive_pct_max: 100,
  fixed_amount_max: 5000,
  fee_multiplier_max: 3,
  grace_minutes_max: 30,
  lead_hours_max: 72,
  free_pass_cap_max: 24
};

// The server refuses unknown fields (strict decode), so a version view's rule is cut to these keys.
export const RULE_INPUT_KEYS = [
  'key',
  'stage',
  'group',
  'priority',
  'on_match',
  'name',
  'description',
  'conditions',
  'actions',
  'teacher',
  'active'
];

// ---- words ----

// 'EARLY_EXIT' / 'teacher_wait_mins' -> 'Early exit' / 'Teacher wait mins'. For codes the API has no label for.
export const words = (code) => {
  const s = String(code || '')
    .replace(/[._]+/g, ' ')
    .trim()
    .toLowerCase();
  return s ? s[0].toUpperCase() + s.slice(1) : '';
};

// Stages run in this fixed order (rules.StageOrder); unknown ones sort last. Fallback for the catalogue.
export const STAGES = [
  ['GATES', 'Scope gates'],
  ['FEE_BASIS', 'Fee basis'],
  ['SESSION_PAY', 'Session pay'],
  ['ATTENDANCE', 'Attendance penalties'],
  ['EXIT', 'Cancellation and replacement penalties'],
  ['RESCHEDULED', 'Late reschedules'],
  ['TRIAL_INCENTIVE', 'Trial incentives'],
  ['ADDON_INCENTIVE', 'Add-on incentives'],
  ['FLAGS', 'Review flags'],
  ['MONTH_SESSION', 'Group class attendance'],
  ['MONTH', 'Month: tax, rounding and Free Pass']
];
export const MODE_TEXT = {
  EXCLUSIVE: 'The first matching rule decides',
  STACK: 'Every matching rule applies'
};

// ---- catalogue (GET /rules/catalogue) ----

/*
 * The labels and allowed month values the server publishes. FALLBACK_CATALOGUE is what the screens
 * use until it loads, or if it fails: stage and mode wording from above, other codes via words.
 */
export const FALLBACK_CATALOGUE = {
  loaded: false,
  stages: STAGES.map(([code, label]) => ({ code, label })),
  stage: Object.fromEntries(STAGES),
  group_mode: { ...MODE_TEXT },
  fact: {},
  penalty: {},
  incentive: {},
  flag: {},
  month_params: {}
};

const listed = (a) => (Array.isArray(a) ? a.filter((x) => x && typeof x === 'object') : []);
const label_map = (a, key = 'code') =>
  Object.fromEntries(
    listed(a)
      .filter((x) => x[key] && x.label)
      .map((x) => [x[key], x.label])
  );
const num_or_null = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export const catalogue_view = (data) => {
  const d = data || {};
  const stages = listed(d.stages)
    .filter((s) => s.code && s.label)
    .map((s) => ({ code: s.code, label: s.label }));
  return {
    loaded: true,
    stages: stages.length ? stages : FALLBACK_CATALOGUE.stages,
    stage: { ...FALLBACK_CATALOGUE.stage, ...label_map(d.stages) },
    group_mode: { ...MODE_TEXT, ...label_map(d.group_modes) },
    fact: label_map(d.facts, 'key'),
    penalty: label_map(d.penalty_types),
    incentive: label_map(d.incentive_categories),
    flag: label_map(d.review_flags),
    month_params: Object.fromEntries(
      listed(d.month_params)
        .filter((p) => p.param)
        .map((p) => [
          p.param,
          {
            label: p.label || '',
            choices: listed(p.choices)
              .filter((c) => typeof c.code === 'string')
              .map((c) => ({ code: c.code, label: c.label || c.code })),
            min: num_or_null(p.min),
            max: num_or_null(p.max)
          }
        ])
    )
  };
};

// kind: 'stage' | 'group_mode' | 'fact' | 'penalty' | 'incentive' | 'flag'.
export const label_of = (cat, kind, code) =>
  ((cat || FALLBACK_CATALOGUE)[kind] || {})[code] || words(code);

const stage_index = (cat, s) => {
  const list = (cat || FALLBACK_CATALOGUE).stages;
  const i = list.findIndex((x) => x.code === s);
  return i === -1 ? list.length : i;
};
export const stage_label = (s, cat = FALLBACK_CATALOGUE) =>
  (cat.stage && cat.stage[s]) || words(s) || EM_DASH;

export const VERSION_STATUS = {
  CURRENT: { label: 'In force', tone: 'green' },
  SCHEDULED: { label: 'Scheduled', tone: 'blue' },
  PAST: { label: 'Past', tone: 'gray' },
  SUPERSEDED: { label: 'Replaced the same day', tone: 'orange' },
  INACTIVE: { label: 'Switched off', tone: 'gray' }
};
export const version_status_view = (s) =>
  VERSION_STATUS[s] || { label: s || EM_DASH, tone: 'gray' };

export const CHANGE_STATUS = {
  PREVIEWED: { label: 'Not saved yet', tone: 'yellow' },
  PENDING_APPROVAL: { label: 'Waiting for approval', tone: 'orange' },
  APPROVED: { label: 'Approved, not saved yet', tone: 'blue' },
  APPLIED: { label: 'Saved', tone: 'green' },
  REJECTED: { label: 'Rejected', tone: 'red' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'gray' },
  STALE: { label: 'Out of date', tone: 'gray' }
};
export const change_status_view = (s) => CHANGE_STATUS[s] || { label: s || EM_DASH, tone: 'gray' };

export const PREVIEW_STATUS = {
  QUEUED: 'Waiting to start',
  RUNNING: 'Running',
  DONE: 'Finished',
  FAILED: 'Failed'
};

export const APPROVAL_REASON_TEXT = {
  TAX_ROUNDING: 'It changes GST, TDS or rounding.',
  BACK_DATED: 'Its date is before today, so it reprices classes already held.',
  BIG_IMPACT: `It moves more than ₹${LIMITS.big_impact_rupees.toLocaleString('en-IN')} (pay increases and decreases counted together), or changes pay for more than ${LIMITS.big_impact_teachers} teachers.`,
  FREE_PASS: 'It changes how Free Pass Days are earned, capped or paid out.',
  IMPACT_UNKNOWN: 'The preview could not price any month, or some months, so its impact is unknown.'
};
// A code this page has no text for still reads as words, never as the raw code.
export const approval_reason_text = (code) => APPROVAL_REASON_TEXT[code] || words(code);
export const preview_status_text = (status) =>
  status ? PREVIEW_STATUS[status] || words(status) : 'Not run';

const CHANGED_FIELD_TEXT = {
  actions: 'values',
  conditions: 'conditions',
  teacher: 'teacher wording',
  name: 'name',
  description: 'description',
  active: 'on/off',
  priority: 'order',
  on_match: 'stop/continue',
  group: 'group',
  mode: 'mode'
};
export const changed_fields_text = (fields) =>
  (Array.isArray(fields) ? fields : []).map((f) => CHANGED_FIELD_TEXT[f] || words(f)).join(', ');

// ---- list ----

const who = (v) => v.created_by_name || v.created_by_email || EM_DASH;

// Amendments write versions without a change_uuid too; only the first, non-amendment one is the seed.
export const is_seed_version = (v) => {
  const x = v || {};
  if (x.change_uuid) return false;
  if (/amendment$/i.test(String(x.created_by_name || '').trim())) return false;
  return Number(x.version_no) === 1;
};

export const version_row = (v) => {
  const r = v.rule || {};
  const g = v.group || {};
  const t = r.teacher || {};
  return {
    key: `${v.rule_key}#${v.version_id}`,
    rule_key: v.rule_key,
    kind: v.kind,
    version_id: v.version_id,
    version_no: v.version_no,
    stage: v.stage,
    group_key: v.kind === 'GROUP' ? v.rule_key : r.group,
    name: r.name || g.name || words(v.rule_key),
    sentence: v.sentence || '',
    teacher_title: t.title || '',
    teacher_why: t.why || '',
    effective_date: v.effective_date || '',
    effective_text: v.effective_date ? version_day_label(v.effective_date) : EM_DASH,
    status: v.status || '',
    active: v.active !== false,
    changed_by: who(v),
    changed_at: v.created_at_ist ? stamp_label(v.created_at_ist) : '',
    is_seed: is_seed_version(v),
    // an amendment edits the rule outside a change, so it names no changed fields
    is_amendment: !v.change_uuid && !is_seed_version(v),
    note: v.note || '',
    changed_fields: Array.isArray(v.changed_fields) ? v.changed_fields : [],
    rule: v.rule || null,
    group: v.group || null
  };
};

// Groups keep the server's order (stage, group priority, rule priority); a rule whose group is not in
// force still shows, under its group key, so nothing is silently dropped.
export const list_view = (data, cat = FALLBACK_CATALOGUE) => {
  const d = data || {};
  const groups = (Array.isArray(d.groups) ? d.groups : []).map(version_row);
  const rules = (Array.isArray(d.rules) ? d.rules : []).map(version_row);
  const by_group = new Map();
  groups.forEach((g) => {
    by_group.set(g.rule_key, {
      key: g.rule_key,
      stage: g.stage,
      name: g.name,
      mode_text: (g.group && g.group.mode && label_of(cat, 'group_mode', g.group.mode)) || '',
      sentence: g.sentence,
      version: g,
      rules: []
    });
  });
  rules.forEach((r) => {
    if (!by_group.has(r.group_key)) {
      by_group.set(r.group_key, {
        key: r.group_key,
        stage: r.stage,
        name: words(r.group_key),
        mode_text: '',
        sentence: '',
        version: null,
        rules: []
      });
    }
    by_group.get(r.group_key).rules.push(r);
  });
  const stages = [];
  [...by_group.values()]
    .sort((a, b) => stage_index(cat, a.stage) - stage_index(cat, b.stage))
    .forEach((g) => {
      let s = stages.find((x) => x.stage === g.stage);
      if (!s) {
        s = { stage: g.stage, label: stage_label(g.stage, cat), groups: [] };
        stages.push(s);
      }
      s.groups.push(g);
    });
  const names = {};
  rules.concat(groups).forEach((r) => {
    names[r.rule_key] = r.name;
  });
  return {
    at: d.at || '',
    today: d.today || '',
    base_rule_set_hash: d.base_rule_set_hash || '',
    earliest_allowed_date: d.earliest_allowed_date || '',
    latest_allowed_date: d.latest_allowed_date || '',
    can_change: d.can_change === true,
    stages,
    rule_count: rules.length,
    scheduled: (Array.isArray(d.scheduled) ? d.scheduled : []).map(version_row),
    names
  };
};

// Search covers what Ops read (name, sentence, teacher wording) and the key, which Engineering quotes.
export const filter_stages = (stages, { q = '', stage = '', status = '' } = {}) => {
  const needle = q.trim().toLowerCase();
  const hit = (r) =>
    (!needle ||
      [r.name, r.sentence, r.teacher_title, r.teacher_why, r.rule_key].some((x) =>
        String(x || '')
          .toLowerCase()
          .includes(needle)
      )) &&
    (!status || (status === 'active' ? r.active : !r.active));
  return stages
    .filter((s) => !stage || s.stage === stage)
    .map((s) => ({
      ...s,
      groups: s.groups
        .map((g) => ({ ...g, rules: g.rules.filter(hit) }))
        .filter((g) => g.rules.length)
    }))
    .filter((s) => s.groups.length);
};

export const scheduled_for = (scheduled, rule_key) =>
  (Array.isArray(scheduled) ? scheduled : []).filter((v) => v.rule_key === rule_key);

// ---- the value-edit form ----

const ANCHOR_TEXT = {
  FINAL_SCHEDULED_START: 'the scheduled start',
  START_IN_EFFECT_AT_EVENT: 'the start in effect at that time',
  SCHEDULED_END: 'the scheduled end',
  LATER_OF_START_AND_STUDENT_JOIN: "the later of the start and the student's join"
};
const CMP_TEXT = {
  MORE_THAN: 'more than',
  AT_LEAST: 'at least',
  LESS_THAN: 'less than',
  AT_MOST: 'at most',
  OTHERWISE: 'otherwise'
};
const OP_TEXT = {
  EQ: 'is',
  NE: 'is not',
  GT: 'is over',
  GTE: 'is at least',
  LT: 'is under',
  LTE: 'is at most'
};
const UNIT_TEXT = { MIN: 'min', HOUR: 'hours' };
const BASIS_TEXT = {
  ADJUSTED_FEE: 'the fee incl. surge',
  PLAIN_FEE: 'the fee',
  SESSION_PAY: 'the session pay',
  FIXED: 'a fixed amount'
};
const TAX_PARAMS = ['gst_pct', 'tds_pct', 'rounding'];
const GRACE_ANCHORS = ['SCHEDULED_END', 'LATER_OF_START_AND_STUDENT_JOIN'];

// a grace or buffer (from the end, the later of start and student join, or any delay AFTER a start,
// e.g. a group class's late join) is 0-30 min; a lead is 0-72 h. Mirrors goserver rulechange
// checkWindow.
export const window_max = (anchor, unit, dir) => {
  if (GRACE_ANCHORS.indexOf(anchor) !== -1 || dir === 'AFTER') {
    return unit === 'HOUR' ? RANGES.grace_minutes_max / 60 : RANGES.grace_minutes_max;
  }
  return unit === 'HOUR' ? RANGES.lead_hours_max : RANGES.lead_hours_max * 60;
};

const num_text = (v) => (v === undefined || v === null ? '0' : String(v));
const minutes_to_hhmm = (m) => {
  const n = Math.round(Number(m) || 0);
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
};

const field = (path, label, value, extra = {}) => ({
  id: path.join('.'),
  path,
  section: 'value',
  input: 'number',
  min: 0,
  max: null,
  integer: false,
  allow_negative: false,
  unit: '',
  hint: '',
  original: value,
  ...extra,
  label
});

const window_field = (path, label, value, anchor, unit, dir) =>
  field(path, label, num_text(value), {
    unit: UNIT_TEXT[unit] || '',
    max: window_max(anchor, unit, dir),
    hint: `Measured from ${ANCHOR_TEXT[anchor] || words(anchor)}.`
  });

const fact_text = (cat, key) => label_of(cat, 'fact', key).toLowerCase();

// In an either/or, a yes/no fact ANDed with a limit says when that limit applies (e.g. PEN.LATE_JOIN v2 of 7 Oct:
// 2 min, or 1 min when the student left first), so two limits on the same measure get distinct labels.
const bool_qualifier = (x, cat) => {
  if (!x || typeof x !== 'object' || !x.fact || x.window) return '';
  if (x.op === 'IS_TRUE') return fact_text(cat, x.fact);
  if (x.op === 'IS_FALSE') return `not: ${fact_text(cat, x.fact)}`;
  return '';
};

// alternative: c is one branch of an 'any'.
const cond_fields = (c, path, out, cat, when = [], alternative = false) => {
  if (!c || typeof c !== 'object') return;
  ['all', 'any'].forEach((k) => {
    const list = Array.isArray(c[k]) ? c[k] : [];
    const own =
      k === 'all' && alternative ? list.map((x) => bool_qualifier(x, cat)).filter(Boolean) : [];
    list.forEach((x, i) =>
      cond_fields(x, [...path, k, i], out, cat, [...when, ...own], k === 'any')
    );
  });
  if (c.not) cond_fields(c.not, [...path, 'not'], out, cat);
  if (c.window) {
    const w = c.window;
    const label = `Time limit: ${fact_text(cat, w.event)} ${
      w.dir === 'AFTER' ? 'after' : 'before'
    } ${ANCHOR_TEXT[w.anchor] || words(w.anchor)}, ${CMP_TEXT[w.cmp] || words(w.cmp)}${
      when.length ? ` (only when ${when.join(' and ')})` : ''
    }`;
    out.push(window_field([...path, 'window', 'value'], label, w.value, w.anchor, w.unit, w.dir));
  }
  const n = c.value && c.value.num;
  if (c.fact && typeof n === 'number' && OP_TEXT[c.op]) {
    const label = `Condition: ${fact_text(cat, c.fact)} ${OP_TEXT[c.op]}`;
    if (c.fact === 'start_minute_of_day') {
      out.push(
        field(
          [...path, 'value', 'num'],
          `Condition: start time ${OP_TEXT[c.op]}`,
          minutes_to_hhmm(n),
          {
            input: 'time',
            hint: 'IST, 00:00 to 23:59.'
          }
        )
      );
    } else {
      out.push(
        field([...path, 'value', 'num'], label, num_text(n), {
          allow_negative: true,
          min: null,
          hint: 'No published range; the server checks it.'
        })
      );
    }
  }
};

const TAX_HINT =
  'Tax changes need approval: after the preview, another teacher-payments user must approve them before they are saved.';
// Used when the catalogue did not load: GST/TDS stay free numbers the server checks.
const PARAM_FALLBACK = {
  free_pass_cap: { label: 'Free Pass balance cap (days)' },
  gst_pct: { label: 'GST (%)', unit: '%' },
  tds_pct: { label: 'TDS (%)', unit: '%' }
};

/*
 * A month parameter (SET_PARAM): a pick from the catalogue's allowed values, or for the Free Pass
 * cap a whole number in its range.
 */
// A code ('HALF_UP') reads as words; a number ('12.5') stays as it is.
const choice_text = (value) => (/^[A-Z][A-Z0-9_]*$/.test(value) ? words(value) : value);

const param_field = (a, path, cat) => {
  const mp = ((cat || FALLBACK_CATALOGUE).month_params || {})[a.param];
  const fb = PARAM_FALLBACK[a.param];
  const value = a.value === undefined || a.value === null ? '' : String(a.value);
  const hint = TAX_PARAMS.indexOf(a.param) !== -1 ? TAX_HINT : '';
  if (mp && mp.choices.length) {
    const choices = mp.choices.some((c) => c.code === value)
      ? mp.choices
      : [
          ...mp.choices,
          { code: value, label: `${choice_text(value) || 'Not set'} (in force, no longer offered)` }
        ];
    return field(path, mp.label || (fb && fb.label) || words(a.param), value, {
      input: 'select',
      choices,
      allowed: mp.choices.map((c) => c.code),
      hint
    });
  }
  if (a.param === 'free_pass_cap') {
    return field(path, (mp && mp.label) || fb.label, value || '0', {
      integer: true,
      min: mp && mp.min !== null ? mp.min : 0,
      max: mp && mp.max !== null ? mp.max : RANGES.free_pass_cap_max,
      unit: mp && mp.label ? 'days' : '',
      as_text: true
    });
  }
  if (fb) {
    return field(path, fb.label, value || '0', { unit: fb.unit || '', as_text: true, hint });
  }
  return null;
};

const action_fields = (a, i, out, cat) => {
  const p = ['actions', i];
  switch (a.type) {
    case 'SET_PAY_PCT':
      out.push(
        field([...p, 'pct'], 'Pay (% of the fee)', num_text(a.pct), {
          unit: '%',
          max: RANGES.pay_pct_max
        })
      );
      break;
    case 'PENALTY':
      out.push(
        field(
          [...p, 'pct'],
          `${label_of(cat, 'penalty', a.penalty_type)} penalty (% of ${BASIS_TEXT[a.basis] || 'the fee'})`,
          num_text(a.pct),
          {
            unit: '%',
            max: RANGES.penalty_pct_max
          }
        )
      );
      break;
    case 'TIERED_PENALTY':
      (Array.isArray(a.bands) ? a.bands : []).forEach((b, j) => {
        const bp = [...p, 'bands', j];
        const name = b.label || words(b.id);
        if (b.cmp !== 'OTHERWISE' && a.measure) {
          out.push(
            window_field(
              [...bp, 'value'],
              `Band "${name}": limit (${CMP_TEXT[b.cmp] || words(b.cmp)})`,
              b.value,
              a.measure.anchor,
              b.unit,
              a.measure.dir
            )
          );
        }
        if (b.penalty_type) {
          out.push(
            field(
              [...bp, 'pct'],
              `Band "${name}": ${label_of(cat, 'penalty', b.penalty_type)} penalty`,
              num_text(b.pct),
              {
                unit: '%',
                max: RANGES.penalty_pct_max
              }
            )
          );
        }
        out.push(
          field([...bp, 'label'], `Band "${words(b.id)}": teacher label`, b.label || '', {
            section: 'wording',
            input: 'text'
          })
        );
      });
      break;
    case 'INCENTIVE':
      if (a.basis === 'FIXED') {
        out.push(
          field(
            [...p, 'amount'],
            `${label_of(cat, 'incentive', a.category)} amount (₹${a.count_fact ? ` per ${fact_text(cat, a.count_fact)}` : ''})`,
            num_text(a.amount),
            { unit: '₹', max: RANGES.fixed_amount_max }
          )
        );
      } else {
        out.push(
          field(
            [...p, 'pct'],
            `${label_of(cat, 'incentive', a.category)} (% of ${BASIS_TEXT[a.basis] || 'the fee'})`,
            num_text(a.pct),
            {
              unit: '%',
              max: RANGES.incentive_pct_max
            }
          )
        );
      }
      break;
    case 'SET_FEE_MULTIPLIER':
      if (a.factor) {
        out.push(
          field([...p, 'factor'], 'Fee factor (x)', num_text(a.factor), {
            max: RANGES.fee_multiplier_max
          })
        );
      } else {
        out.push(
          field([...p, 'multiplier'], 'Fee multiplier (x base rate)', num_text(a.multiplier), {
            max: RANGES.fee_multiplier_max
          })
        );
      }
      break;
    case 'LINEAR_PCT':
      out.push(
        field([...p, 'a'], `Formula: % per unit of ${fact_text(cat, a.fact)}`, num_text(a.a), {
          allow_negative: true,
          min: null,
          hint: 'No published range; the server checks it.'
        })
      );
      out.push(
        field([...p, 'b'], 'Formula: constant (%)', num_text(a.b), {
          allow_negative: true,
          min: null,
          hint: 'No published range; the server checks it.'
        })
      );
      break;
    case 'SET_PARAM': {
      const f = param_field(a, [...p, 'value'], cat);
      if (f) out.push(f);
      break;
    }
    case 'ACCRUE_FREE_PASS':
      out.push(field([...p, 'days'], 'Free Pass Days earned', num_text(a.days), { integer: true }));
      break;
    default:
      break;
  }
};

// The editable values and wording of one rule, in reading order (actions first, then the conditions).
export const editable_fields = (rule, cat = FALLBACK_CATALOGUE) => {
  const r = rule || {};
  const out = [];
  (Array.isArray(r.actions) ? r.actions : []).forEach((a, i) =>
    action_fields(a || {}, i, out, cat)
  );
  cond_fields(r.conditions, ['conditions'], out, cat);
  const wording = [
    field(['name'], 'Name (Ops)', r.name || '', {
      section: 'wording',
      input: 'text',
      required: true
    }),
    field(['description'], 'Description (Ops)', r.description || '', {
      section: 'wording',
      input: 'textarea'
    })
  ];
  if (r.teacher) {
    wording.push(
      field(['teacher', 'title'], 'Teacher title', r.teacher.title || '', {
        section: 'wording',
        input: 'text'
      }),
      field(['teacher', 'why'], 'Teacher explanation', r.teacher.why || '', {
        section: 'wording',
        input: 'textarea',
        hint: 'Placeholders like {measured}, {limit}, {pct} and {basis} are filled from each class.'
      }),
      field(['teacher', 'footnote'], 'Teacher footnote', r.teacher.footnote || '', {
        section: 'wording',
        input: 'text'
      }),
      field(
        ['teacher', 'band_label'],
        'Teacher label among the alternatives',
        r.teacher.band_label || '',
        {
          section: 'wording',
          input: 'text'
        }
      )
    );
  }
  // Band labels sit with the bands above but are wording, so they move to the wording section.
  return [
    ...out.filter((f) => f.section === 'value'),
    ...wording,
    ...out.filter((f) => f.section === 'wording')
  ];
};

export const initial_values = (fields) => Object.fromEntries(fields.map((f) => [f.id, f.original]));

// '%' sits on the number; other units follow a space ('30 min').
export const unit_suffix = (unit) => {
  if (!unit) return '';
  return unit === '%' ? '%' : ` ${unit}`;
};

const NUM_RE = /^\d+(\.\d+)?$/;
const SIGNED_RE = /^-?\d+(\.\d+)?$/;
const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

// Field id -> message; '' values read as missing.
export const value_error = (f, raw) => {
  const s = String(raw === undefined || raw === null ? '' : raw).trim();
  if (f.input === 'text' || f.input === 'textarea') {
    return f.required && !s ? 'This cannot be empty.' : '';
  }
  if (f.input === 'select') {
    return s === String(f.original).trim() || (f.allowed || []).indexOf(s) !== -1
      ? ''
      : 'Pick one of the allowed values.';
  }
  if (!s) return 'Enter a value.';
  if (f.input === 'time') return HHMM_RE.test(s) ? '' : 'Enter a time between 00:00 and 23:59.';
  if (!(f.allow_negative ? SIGNED_RE : NUM_RE).test(s)) {
    return f.allow_negative ? 'Enter a number.' : 'Enter a number of 0 or more.';
  }
  const n = Number(s);
  if (f.integer && !Number.isInteger(n)) return 'Enter a whole number.';
  if (f.min !== null && f.min !== undefined && n < f.min)
    return `The lowest allowed value is ${f.min}.`;
  if (f.max !== null && f.max !== undefined && n > f.max) {
    return `The highest allowed value is ${f.max}${unit_suffix(f.unit)}.`;
  }
  return '';
};

const parse_value = (f, raw) => {
  const s = String(raw).trim();
  if (f.input === 'text' || f.input === 'textarea' || f.input === 'select') return s;
  if (f.input === 'time') {
    const m = s.match(HHMM_RE);
    return Number(m[1]) * 60 + Number(m[2]);
  }
  return f.as_text ? String(Number(s)) : Number(s);
};

const set_in = (obj, path, value) => {
  let o = obj;
  for (let i = 0; i < path.length - 1; i += 1) {
    if (o[path[i]] === undefined || o[path[i]] === null) o[path[i]] = {};
    o = o[path[i]];
  }
  o[path[path.length - 1]] = value;
};

// The rule as the server takes it: the in-force content with the edits, cut to the input keys.
export const edited_rule = (rule, fields, values) => {
  const r = JSON.parse(JSON.stringify(rule || {}));
  // An untouched field is left as stored, so an omitted zero (Go omitempty) never reads as an edit.
  fields.forEach((f) => {
    const raw = values[f.id];
    if (raw === undefined || String(raw).trim() === String(f.original).trim()) return;
    if (!value_error(f, raw)) set_in(r, f.path, parse_value(f, raw));
  });
  if (r.teacher) {
    // Optional teacher fields go back as the seed stores them: absent rather than empty.
    ['footnote', 'band_label'].forEach((k) => {
      if (r.teacher[k] === '') delete r.teacher[k];
    });
  }
  return Object.fromEntries(
    RULE_INPUT_KEYS.filter((k) => r[k] !== undefined).map((k) => [k, r[k]])
  );
};

const canonical = (v) => {
  if (Array.isArray(v)) return v.map(canonical);
  if (v && typeof v === 'object') {
    return Object.fromEntries(
      Object.keys(v)
        .filter((k) => v[k] !== undefined)
        .sort()
        .map((k) => [k, canonical(v[k])])
    );
  }
  return v;
};
export const same_json = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));

// What the server counts as teacher-visible or tax (rulechange/candidates.go teacherVisible / setsTax).
const VISIBLE_ACTIONS = [
  'SET_PAY_PCT',
  'PENALTY',
  'TIERED_PENALTY',
  'INCENTIVE',
  'LINEAR_PCT',
  'ACCRUE_FREE_PASS',
  'SET_PARAM'
];
const actions_of = (r) => (r && Array.isArray(r.actions) ? r.actions : []);
export const teacher_visible = (rule) =>
  actions_of(rule).some((a) => VISIBLE_ACTIONS.indexOf(a && a.type) !== -1);
export const sets_tax = (rule) =>
  actions_of(rule).some((a) => a && a.type === 'SET_PARAM' && TAX_PARAMS.indexOf(a.param) !== -1);

// Mirrors goserver setsFreePass: earns Free Pass Days, or sets the cap or encashment basis.
const FREE_PASS_PARAMS = ['free_pass_cap', 'encash_basis'];
export const sets_free_pass = (rule) =>
  actions_of(rule).some(
    (a) =>
      a &&
      (a.type === 'ACCRUE_FREE_PASS' ||
        (a.type === 'SET_PARAM' && FREE_PASS_PARAMS.indexOf(a.param) !== -1))
  );

// Same rule once names, descriptions, teacher text and band labels are set aside (server sameContent).
export const wording_only = (before, after) => {
  if (!before || !after) return false;
  const strip = (r) => ({
    ...r,
    name: undefined,
    description: undefined,
    teacher: undefined,
    actions: actions_of(r).map((a) =>
      a && Array.isArray(a.bands)
        ? { ...a, bands: a.bands.map((b) => ({ ...b, label: undefined })) }
        : a
    )
  });
  return same_json(strip(before), strip(after));
};

// Code points, as the server counts runes.
export const text_length = (s) => (typeof s === 'string' ? [...s.trim()].length : 0);

// what: 'note' -> 'Add a note.' / 'The note needs at least 10 characters.'
const length_error = (s, min, max, what) => {
  const n = text_length(s);
  if (n === 0) return `Add a ${what}.`;
  if (n < min) return `The ${what} needs at least ${min} characters.`;
  if (n > max) return `The ${what} can be at most ${max} characters.`;
  return '';
};

/*
 * Every form error, keyed by field id or form field name. ctx: { fields, rule (as in force),
 * earliest, latest }.
 */
export const form_errors = (form, ctx) => {
  const f = form || {};
  const c = ctx || {};
  const errors = {};
  (c.fields || []).forEach((fd) => {
    const e = value_error(fd, (f.values || {})[fd.id]);
    if (e) errors[fd.id] = e;
  });
  if (
    !Object.keys(errors).length &&
    c.rule &&
    same_json(edited_rule(c.rule, c.fields || [], f.values || {}), edited_rule(c.rule, [], {}))
  ) {
    errors.values = 'Change at least one value or wording before previewing.';
  }
  const d = f.effective_date || '';
  if (!d) errors.effective_date = 'Choose the date the change takes effect.';
  else if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || day_label(d) === d)
    errors.effective_date = 'Enter a valid date.';
  else if (c.earliest && d < c.earliest)
    errors.effective_date = `The earliest allowed date is ${day_label(c.earliest)}.`;
  else if (c.latest && d > c.latest)
    errors.effective_date = `The latest allowed date is ${day_label(c.latest)}.`;

  const note = length_error(f.note, LIMITS.note_min_len, LIMITS.note_max_len, 'note');
  if (note) errors.note = note;

  const edited = c.rule ? edited_rule(c.rule, c.fields || [], f.values || {}) : null;
  const visible = teacher_visible(c.rule) || teacher_visible(edited);
  if (f.notice_mode === 'notice') {
    const e = length_error(
      f.teacher_notice,
      LIMITS.notice_min_len,
      LIMITS.notice_max_len,
      'teacher notice'
    );
    if (e) errors.teacher_notice = e;
  } else if (f.notice_mode === 'none') {
    const e = length_error(
      f.no_notice_reason,
      LIMITS.no_notice_min_len,
      LIMITS.no_notice_max_len,
      'reason for no notice'
    );
    if (e) errors.no_notice_reason = e;
  } else if (visible) {
    errors.notice_mode =
      'Teachers see this rule: write a notice for them, or say why none is needed.';
  }
  return errors;
};

// Form-level errors in the order their controls appear, after the value fields.
const FORM_ERROR_ORDER = [
  'values',
  'effective_date',
  'note',
  'notice_mode',
  'teacher_notice',
  'no_notice_reason'
];
// The first error in on-screen order (field_ids: the value fields as rendered), for focus on submit.
export const first_error_key = (errors, field_ids = []) =>
  [...field_ids, ...FORM_ERROR_ORDER].find((k) => errors && errors[k]) || '';

// Approval reasons the form can already see (the server adds BIG_IMPACT once the preview is done).
export const expected_approval = (form, rule_before, rule_after, today) => {
  const out = [];
  if (sets_tax(rule_before) || sets_tax(rule_after)) out.push('TAX_ROUNDING');
  if (form && form.effective_date && today && form.effective_date < today) out.push('BACK_DATED');
  if (
    (sets_free_pass(rule_before) || sets_free_pass(rule_after)) &&
    !wording_only(rule_before, rule_after)
  )
    out.push('FREE_PASS');
  return out;
};

// The POST /rules/changes body. Only the chosen notice field carries text; the other goes empty.
export const create_body = ({ client_request_id, base_rule_set_hash, form, rule, fields }) => ({
  client_request_id,
  base_rule_set_hash,
  effective_date: form.effective_date,
  note: (form.note || '').trim(),
  teacher_notice: form.notice_mode === 'notice' ? (form.teacher_notice || '').trim() : '',
  no_notice_reason: form.notice_mode === 'none' ? (form.no_notice_reason || '').trim() : '',
  versions: [{ kind: 'RULE', rule: edited_rule(rule, fields, form.values || {}) }]
});

/* Idempotency: one client_request_id per request content. */
export const request_id_for = (prev, body_without_id, new_id) => {
  const sig = JSON.stringify(canonical(body_without_id));
  if (prev && prev.sig === sig && prev.id) return prev;
  return { sig, id: new_id() };
};

// ---- refusals ----

export const ERROR_TEXT = {
  INVALID_REQUEST: 'The request could not be read. Reload the page and try again.',
  INVALID_DATE: 'Enter a valid effective date.',
  DATE_BEFORE_ALLOWED: 'That date falls in or before a closed month. Pick a later date.',
  DATE_TOO_FAR: 'That date is more than 90 days ahead. Pick an earlier date.',
  NOTE_REQUIRED: `Add a note of ${LIMITS.note_min_len} to ${LIMITS.note_max_len} characters saying why the rules change.`,
  NOTICE_REQUIRED:
    'This change touches a rule teachers see: write a teacher notice, or give a reason for no notice (not both).',
  INVALID_VERSIONS: 'The change has problems. Fix them and preview again.',
  RULES_CHANGED:
    'Someone changed the rules after you started. Close this, reload the rules and start the change again.',
  IDEMPOTENCY_MISMATCH:
    'This request was already used for a different change. Press Preview impact again.',
  RULE_NOT_FOUND: 'That rule was not found.',
  CHANGE_NOT_FOUND: 'That rule change was not found.',
  PREVIEW_NOT_FOUND: 'That preview was not found. Re-run the preview.',
  PREVIEW_NOT_READY: 'The preview has not finished. Wait for it, or re-run it, before saving.',
  PREVIEW_STALE: 'The preview is more than 24 hours old. Re-run it, then save.',
  PREVIEW_BLOCKED:
    'The preview found teacher-months or classes the new rules cannot price. Fix the change and preview again.',
  CHANGE_NOT_OPEN: 'This change can no longer be saved or previewed.',
  APPROVAL_REQUIRED:
    'This change needs approval. Ask for approval: another teacher-payments user must approve it before it can be saved.',
  APPROVAL_NOT_NEEDED: 'This change does not need approval. Save it.',
  APPROVAL_STALE:
    'The preview or the approval is out of date. Reload the change and look at its newest preview.',
  SELF_APPROVAL:
    'You prepared this change or asked for its approval, so someone else must approve or reject it.',
  NOT_MAKER:
    'Only the person who prepared this change or asked for its approval can withdraw it. You can reject it instead.',
  NOT_REVIEWER:
    'Only the person who approved this change can cancel the approval. Re-running its preview also cancels it.',
  FORBIDDEN: 'You do not have permission to change payment rules.',
  LOOKUP_FAILED: 'Something went wrong on our side. Reload to check what was saved, then try again.'
};

const is_code = (s) => /^[A-Z][A-Z0-9_]*$/.test(s);

// { code, text, problems, approval_reasons, fields, permission, dropped }. dropped = no answer came back,
// so the write may or may not have happened (the retry case).
export const refusal = (err, fallback) => {
  const body = (err && err.response && err.response.data) || {};
  const { code, message } = api_error(err);
  const permission = is_permission_error(err) || code === 'FORBIDDEN';
  const status = err && err.response && err.response.status;
  const text =
    (message && !is_code(message) && message) ||
    ERROR_TEXT[code] ||
    (permission ? ERROR_TEXT.FORBIDDEN : '') ||
    fallback;
  return {
    code,
    text,
    permission,
    dropped: !status || status >= 500,
    problems: (Array.isArray(body.problems) ? body.problems : []).map((p) => ({
      rule_key: p.rule_key || '',
      message: p.message || ''
    })),
    approval_reasons: (Array.isArray(body.approval_reasons) ? body.approval_reasons : []).map(
      approval_reason_text
    ),
    fields: Array.isArray(body.fields) ? body.fields : []
  };
};

// ---- preview and save ----

const MS_HOUR = 3600 * 1000;

// '2026-10-06 15:40' (IST) -> epoch ms; NaN when unreadable.
export const ist_stamp_ms = (s) => {
  const m = typeof s === 'string' ? s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/) : null;
  if (!m) return NaN;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) - IST_OFFSET_MS;
};

// The server's stamp has minute precision, so a preview reads stale up to a minute early, never late.
export const preview_is_fresh = (preview, now) => {
  if (!preview || preview.status !== 'DONE') return false;
  const t = ist_stamp_ms(preview.finished_at_ist);
  if (!Number.isFinite(t)) return false;
  return now.getTime() - t < LIMITS.preview_max_age_hours * MS_HOUR;
};

export const preview_running = (preview) =>
  !!preview && (preview.status === 'QUEUED' || preview.status === 'RUNNING');

const blocking_total = (s) =>
  s ? (Number(s.blocking_count) || 0) + (Number(s.lost_pay_decisions) || 0) : 0;

/* Whether "Save" may be pressed now, and why not. */
export const apply_state = (change, now) => {
  const c = change || {};
  const p = c.preview;
  if (c.status === 'APPLIED') return { enabled: false, reason: '' };
  if (c.status === 'PENDING_APPROVAL') {
    const can_approve = !!(c.viewer && c.viewer.can_approve);
    return {
      enabled: false,
      reason: can_approve
        ? 'Approve it first (below), then save.'
        : 'Waiting for approval from another teacher-payments user.'
    };
  }
  if (c.status !== 'PREVIEWED' && c.status !== 'APPROVED') {
    return {
      enabled: false,
      reason: `This change is ${change_status_view(c.status).label.toLowerCase()} and cannot be saved.`
    };
  }
  if (!p) return { enabled: false, reason: 'Run a preview before saving.' };
  if (preview_running(p)) return { enabled: false, reason: 'Wait for the preview to finish.' };
  if (p.status === 'FAILED')
    return { enabled: false, reason: p.error || 'The preview failed. Re-run it.' };
  if (!preview_is_fresh(p, now)) return { enabled: false, reason: ERROR_TEXT.PREVIEW_STALE };
  if (blocking_total(p.summary) > 0) return { enabled: false, reason: ERROR_TEXT.PREVIEW_BLOCKED };
  if (c.can_apply !== true) {
    const b = (Array.isArray(c.blockers) ? c.blockers : [])[0];
    return {
      enabled: false,
      reason: (b && (b.message || ERROR_TEXT[b.code])) || 'This change cannot be saved yet.'
    };
  }
  return { enabled: true, reason: '' };
};

// Re-running an approved change's preview cancels the approval (it was of the old preview).
const RERUN_STATUSES = ['PREVIEWED', 'PENDING_APPROVAL', 'APPROVED'];
export const can_rerun = (change) =>
  !!change && RERUN_STATUSES.includes(change.status) && !preview_running(change.preview);

export const progress_view = (preview) => {
  const p = (preview && preview.progress) || {};
  const total = Number(p.total) || 0;
  const done = Math.min(Number(p.done) || 0, total);
  return {
    done,
    total,
    text: total ? `${fmt_count(done)} of ${fmt_count(total)} teacher-months priced` : ''
  };
};

const month_text = (m) => {
  const x = typeof m === 'string' ? m.match(/^(\d{4})-(\d{2})/) : null;
  return x ? day_label(`${x[1]}-${x[2]}-01`).replace(/^1 /, '') : m || '';
};
export { month_text };

// kind: the catalogue list the codes come from ('penalty' | 'incentive'); session pay has one code.
const impact_rows = (list, cat, kind) =>
  (Array.isArray(list) ? list : []).map((t) => ({
    key: t.code,
    label: t.code === 'payment' ? 'Session pay' : label_of(cat, kind, t.code),
    added: Number(t.added) || 0,
    removed: Number(t.removed) || 0,
    changed: Number(t.changed) || 0,
    delta_text: fmt_signed(t.delta, 2)
  }));

// A teacher named by the preview; a teacher row that is gone has no name or uuid, so the id shows.
export const teacher_ref = (t) => {
  const x = t || {};
  return {
    key: String(x.teacher_uuid || x.teacher_id || ''),
    name: x.teacher_name || (x.teacher_id ? `Teacher ID ${x.teacher_id}` : 'Unknown teacher'),
    uuid: x.teacher_uuid || ''
  };
};

const month_rows = (list) =>
  (Array.isArray(list) ? list : []).map((b) => {
    const teacher = teacher_ref(b);
    const month = month_text(b.pay_month);
    return {
      key: `${b.teacher_id}-${b.pay_month}`,
      teacher,
      month,
      text: `${teacher.name}, ${month}`,
      error: b.error || ''
    };
  });

// Older summaries carry only going_negative ids; those still list, by id.
const going_negative_rows = (s) => {
  const named = Array.isArray(s.going_negative_teachers) ? s.going_negative_teachers : [];
  const ids = Array.isArray(s.going_negative) ? s.going_negative : [];
  return (named.length ? named : ids.map((teacher_id) => ({ teacher_id }))).map(teacher_ref);
};

export const summary_view = (summary, names = {}, cat = FALLBACK_CATALOGUE) => {
  const s = summary || {};
  const name = (k) => names[k] || words(k);
  const flag_rows = (list) =>
    (Array.isArray(list) ? list : []).map((f) => ({
      label: label_of(cat, 'flag', f.reason),
      count: Number(f.count) || 0,
      blocks_close: f.blocks_close === true
    }));
  return {
    teachers_text: `${fmt_count(s.teachers_changed || 0)} of ${fmt_count(s.teachers_total || 0)}`,
    teacher_months: Number(s.teacher_months) || 0,
    total_delta_text: fmt_signed(s.total_delta || 0),
    abs_delta_text: fmt(s.abs_delta || 0),
    free_pass_days: Number(s.free_pass_days) || 0,
    types: [
      ...impact_rows(s.session_pay ? [s.session_pay] : [], cat, ''),
      ...impact_rows(s.penalties, cat, 'penalty'),
      ...impact_rows(s.incentives, cat, 'incentive')
    ].filter((t) => t.added || t.removed || t.changed),
    flags_added: flag_rows(s.flags_added),
    flags_removed: flag_rows(s.flags_removed),
    going_negative: Array.isArray(s.going_negative) ? s.going_negative.length : 0,
    going_negative_teachers: going_negative_rows(s),
    matches: (Array.isArray(s.rule_matches) ? s.rule_matches : []).map((m) => ({
      key: m.rule_key,
      name: name(m.rule_key),
      before:
        m.sessions_before === null || m.sessions_before === undefined
          ? EM_DASH
          : fmt_count(m.sessions_before),
      after:
        m.sessions_after === null || m.sessions_after === undefined
          ? EM_DASH
          : fmt_count(m.sessions_after),
      matches_nothing: m.matches_nothing === true
    })),
    samples: (Array.isArray(s.samples) ? s.samples : []).map((x, i) => ({
      key: `${x.rule_key}-${x.session_id}-${i}`,
      rule_name: name(x.rule_key),
      title: x.title || '',
      text: x.text || '',
      footnote: x.footnote || '',
      complete: x.complete !== false,
      teacher: x.teacher_id || x.teacher_uuid ? teacher_ref(x) : null
    })),
    unpriced_count: Number(s.unpriced_count) || 0,
    unpriced: month_rows(s.unpriced),
    blocking_count: Number(s.blocking_count) || 0,
    blocking: month_rows(s.blocking),
    lost_pay_decisions: Number(s.lost_pay_decisions) || 0
  };
};

// Engine comparison line names ("penalty CANCEL_1", "flag NO_JOIN_DATA", "payable") in Ops words.
const LINE_TEXT = {
  payment: 'Session pay',
  payable: 'Payable',
  gross: 'Gross',
  gst: 'GST',
  tds: 'TDS',
  'free passes placed': 'Free Pass Days placed',
  'carry forward': 'Carried forward'
};
const LINE_KIND = {
  penalty: ['Penalty', 'penalty'],
  incentive: ['Incentive', 'incentive'],
  flag: ['Review flag', 'flag']
};
export const line_label = (line, cat = FALLBACK_CATALOGUE) => {
  const s = String(line || '');
  if (LINE_TEXT[s]) return LINE_TEXT[s];
  const m = s.match(/^(penalty|incentive|flag) (.+)$/);
  if (m) return `${LINE_KIND[m[1]][0]}: ${label_of(cat, LINE_KIND[m[1]][1], m[2])}`;
  return words(s);
};

const is_set = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

// The exact paise field when the server sends it, else the whole-rupee one (delta has no paise on older lines).
export const preview_amount = (paise, rupees) =>
  is_set(paise)
    ? { value: Number(paise) / 100, decimals: 2 }
    : { value: Number(rupees) || 0, decimals: 0 };

export const preview_lines_view = (data, cat = FALLBACK_CATALOGUE) => {
  const d = data || {};
  return {
    total: Number(d.total) || 0,
    rows: (Array.isArray(d.rows) ? d.rows : []).map((r) => {
      const before = preview_amount(r.old_payable_paise);
      const after = preview_amount(r.new_payable_paise);
      const delta = preview_amount(r.delta_paise, r.delta);
      return {
        key: `${r.teacher_uuid || r.teacher_id}-${r.pay_month}`,
        teacher_name: r.teacher_name || `Teacher ID ${r.teacher_id}`,
        month: month_text(r.pay_month),
        old_payable: before.value,
        new_payable: after.value,
        delta: delta.value,
        old_text: fmt(before.value, before.decimals),
        new_text: fmt(after.value, after.decimals),
        delta_text: fmt_signed(delta.value, delta.decimals),
        lines: (Array.isArray(r.lines) ? r.lines : []).map((l, i) => ({
          key: `${l.session_id}-${l.line}-${i}`,
          where: l.session_id ? `Class ${l.session_id}` : 'Month',
          label: line_label(l.line, cat),
          old_text: fmt(l.old, 2),
          new_text: fmt(l.new, 2)
        }))
      };
    })
  };
};

export const saved_text = (change) => {
  const c = change || {};
  const n = Number(c.recalc_months) || 0;
  const when = day_label(c.effective_date);
  const head = `Saved. The new rule takes effect on ${when}.`;
  if (c.wording_only) return `${head} Only wording changed, so no payment is recalculated.`;
  return n
    ? `${head} ${fmt_count(n)} open teacher-month${n === 1 ? '' : 's'} marked for recalculation.`
    : `${head} No open teacher-month needed recalculation.`;
};

// ---- rule references on payment lines ----

/*
 * A pay or penalty line the rules engine priced names its rule version; coded-engine and Ops-added
 * lines carry nulls.
 */
export const rule_ref_of = (row) => {
  const r = row || {};
  const key = typeof r.rule_key === 'string' ? r.rule_key.trim() : '';
  const id = Number(r.rule_version_id);
  const name = typeof r.rule_name === 'string' ? r.rule_name.trim() : '';
  if (!key || !name || !Number.isInteger(id) || id <= 0) return null;
  const no = Number(r.rule_version_no);
  const parts = [
    name,
    Number.isInteger(no) && no > 0 ? ` v${no}` : '',
    r.rule_effective_date ? `, from ${version_day_label(r.rule_effective_date)}` : ''
  ];
  return { rule_key: key, version_id: id, text: `Rule: ${parts.join('')}` };
};

// Change-log row -> the rule and group names it touches (rules[] in rule_keys order; older rows lack it).
export const change_rules_text = (c, names = {}) => {
  const x = c || {};
  const rules = Array.isArray(x.rules) ? x.rules : [];
  const list = rules.length
    ? rules.map((r) => (r && (r.name || names[r.rule_key])) || words(r && r.rule_key))
    : (Array.isArray(x.rule_keys) ? x.rule_keys : []).map((k) => names[k] || words(k));
  return list.filter(Boolean).join(', ');
};

// A change-log page onto the rows held: page 1 replaces, an older page appends. De-duped by change_uuid,
// since a change saved between two page reads shifts the server's window by one.
export const merge_changes = (rows, page, append) => {
  const out = append && Array.isArray(rows) ? [...rows] : [];
  const seen = new Set(out.map((c) => c.change_uuid));
  (Array.isArray(page) ? page : []).forEach((c) => {
    if (!c || seen.has(c.change_uuid)) return;
    seen.add(c.change_uuid);
    out.push(c);
  });
  return out;
};

// What the change-log card shows: a loading line on the first read, no table under a failed read,
// else the table (whose empty text is then true). A page-1 reload keeps the rows it has meanwhile.
export const change_log_mode = (status, count) => {
  if (status === 'error') return 'error';
  if (status === 'loading' && !count) return 'loading';
  return 'table';
};

// A change's version -> the name to show: the page's name, the change's own rule or group name, else words.
export const change_version_name = (v, names = {}) => {
  const x = v || {};
  const after = x.after || {};
  return (
    names[x.rule_key] ||
    (after.rule && after.rule.name) ||
    (x.kind === 'GROUP' && after.group && after.group.name) ||
    words(x.rule_key)
  );
};

// The refusal shape for a check made here before any request (e.g. the preview went stale while idle).
export const local_refusal = (text) => ({
  code: '',
  text,
  permission: false,
  dropped: false,
  problems: [],
  approval_reasons: [],
  fields: []
});

// 'Early exit (v3, from 1 Oct 2026)' for the history pop-up's heading.
export const version_title = (v) =>
  v ? `${version_row(v).name} (v${v.version_no}, from ${version_day_label(v.effective_date)})` : '';

// ---- approvals ----

// A reason or review note: required unless optional, 10-1000 characters once trimmed.
export const approval_note_error = (note, optional = false) => {
  const n = text_length(note);
  if (optional && n === 0) return '';
  if (n < LIMITS.approval_note_min_len)
    return `Write at least ${LIMITS.approval_note_min_len} characters.`;
  if (n > LIMITS.approval_note_max_len)
    return `Keep it to ${LIMITS.approval_note_max_len} characters.`;
  return '';
};

const who_when = (actor, at) =>
  `${(actor && (actor.name || actor.email)) || EM_DASH}${at ? ` on ${stamp_label(at)}` : ''}`;

const codes_of = (list) => (Array.isArray(list) ? list : []);

export const approval_view = (change) => {
  const c = change || {};
  const v = c.viewer || {};
  const a = c.approval || null;
  const live = codes_of(c.approval_reasons);
  const asked = codes_of(a && a.reasons);
  const preview_done = !!(c.preview && c.preview.status === 'DONE');
  const undecided = c.status === 'PREVIEWED' || c.status === 'PENDING_APPROVAL';
  // A running re-run has no impact yet, so its live list lacks the money reasons: keep the request's.
  const use_live = undecided && (preview_done || !asked.length);
  const codes = use_live ? live : asked.length ? asked : live;
  const differs =
    c.status === 'PENDING_APPROVAL' &&
    use_live &&
    asked.length > 0 &&
    (codes.length !== asked.length || codes.some((x) => !asked.includes(x)));
  const added = differs ? codes.filter((x) => !asked.includes(x)) : [];
  let changed_text = '';
  if (differs) {
    changed_text = added.length
      ? 'The preview was re-run since approval was asked. It now also needs approval because:'
      : 'The preview was re-run since approval was asked. The reasons above are the current ones.';
  }
  const blocker = v.approval_blocker
    ? v.approval_blocker.message || ERROR_TEXT[v.approval_blocker.code] || ''
    : '';
  const out = {
    mode: '',
    reasons: codes.map(approval_reason_text),
    impact_unknown: codes.includes('IMPACT_UNKNOWN'),
    asked_reasons: differs ? asked.map(approval_reason_text) : [],
    changed_text,
    added_reasons: added.map(approval_reason_text),
    is_maker: !!v.is_maker,
    can_request: !!v.can_request_approval,
    can_approve: !!v.can_approve,
    can_reject: !!v.can_reject,
    // a draft nobody was asked to approve is discarded from the panel footer, not withdrawn here
    can_withdraw: !!v.can_withdraw && c.status !== 'PREVIEWED',
    can_cancel_approval: !!v.can_cancel_approval,
    blocker,
    asker_name: (a && a.requested_by && (a.requested_by.name || a.requested_by.email)) || '',
    asked_line: a ? `Asked by ${who_when(a.requested_by, a.requested_at_ist)}` : '',
    asked_note: (a && a.request_note) || '',
    reviewed_line: '',
    review_note: (a && a.review_note) || '',
    withdrawn_line: ''
  };
  switch (c.status) {
    case 'PREVIEWED':
      out.mode = codes.length || out.can_request ? 'request' : '';
      break;
    case 'PENDING_APPROVAL':
      out.mode = 'waiting';
      break;
    case 'APPROVED':
      out.mode = 'approved';
      out.reviewed_line = a ? `Approved by ${who_when(a.reviewed_by, a.reviewed_at_ist)}` : '';
      break;
    case 'REJECTED':
      out.mode = 'rejected';
      out.reviewed_line = a ? `Rejected by ${who_when(a.reviewed_by, a.reviewed_at_ist)}` : '';
      break;
    case 'WITHDRAWN':
      out.mode = 'withdrawn';
      out.withdrawn_line =
        a && a.withdrawn_by
          ? `Withdrawn by ${who_when(a.withdrawn_by, a.withdrawn_at_ist)}. Nothing was saved.`
          : 'Withdrawn. Nothing was saved.';
      break;
    case 'APPLIED':
      out.mode = a && a.reviewed_by ? 'applied' : '';
      out.reviewed_line =
        a && a.reviewed_by ? `Approved by ${who_when(a.reviewed_by, a.reviewed_at_ist)}` : '';
      break;
    default:
  }
  return out;
};

// The maker may drop a draft never sent for approval (the withdraw op; no approvals post).
export const can_discard = (change) => {
  const c = change || {};
  return c.status === 'PREVIEWED' && !!(c.viewer && c.viewer.can_withdraw);
};

// One row of the "Waiting for approval" queue, worded: impact from the newest finished preview, a
// blocker when the change can never be approved, and the action the reader can take.
export const approval_row = (item, names = {}) => {
  const x = item || {};
  const a = x.approval || {};
  const imp = x.impact || null;
  let action = 'View';
  if (x.can_review) action = 'Review';
  else if (x.status === 'APPROVED' && !x.blocker) action = 'Save';
  return {
    key: x.change_uuid,
    change_uuid: x.change_uuid,
    rules: change_rules_text(x, names),
    effective: day_label(x.effective_date),
    reasons: (Array.isArray(a.reasons) ? a.reasons : []).map(approval_reason_text),
    asked_by: (a.requested_by && a.requested_by.name) || EM_DASH,
    asked_at: a.requested_at_ist ? stamp_label(a.requested_at_ist) : EM_DASH,
    status: change_status_view(x.status),
    moved: imp ? fmt(imp.abs_delta) : EM_DASH,
    teachers: imp ? fmt_count(imp.teachers_changed) : EM_DASH,
    blocker: x.blocker ? x.blocker.message || ERROR_TEXT[x.blocker.code] || '' : '',
    can_review: !!x.can_review,
    action
  };
};

// The queue's heading count and whether the server cut the list at its cap.
// An older goserver (Hridaya deploys first) has no approvals endpoint: hide the queue and badge quietly.
export const approvals_missing = (err) => !!(err && err.response && err.response.status === 404);

export const queue_view = (data) => {
  const d = data || {};
  const rows = Array.isArray(d.changes) ? d.changes : [];
  return {
    rows,
    waiting: rows.filter((r) => r && r.status === 'PENDING_APPROVAL').length,
    truncated: d.truncated === true,
    truncated_text:
      d.truncated === true ? `Showing the oldest ${fmt_count(LIMITS.approval_queue_cap)}.` : ''
  };
};

// The banner after an approval write succeeded.
export const APPROVAL_DONE_TEXT = {
  request: 'Sent for approval. Another teacher-payments user can now approve or reject it.',
  approve: 'Approved. Anyone with teacher payments access can now save it.',
  cancel_approval: 'Approval cancelled. It is waiting for approval again.',
  reject: 'Rejected. The person who asked can read your note here.',
  withdraw: 'Withdrawn. Nothing was saved.',
  discard: 'Draft discarded. Nothing was saved.'
};

// ---- the 24-hour window, the date drift and starting again ----

const pad2 = (n) => String(n).padStart(2, '0');
// epoch ms -> { day: 'YYYY-MM-DD', clock: '3:40 pm' } in IST.
const ist_parts_of = (ms) => {
  const d = new Date(ms + IST_OFFSET_MS);
  const h = d.getUTCHours();
  return {
    day: `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`,
    clock: `${h % 12 || 12}:${pad2(d.getUTCMinutes())} ${h < 12 ? 'am' : 'pm'}`
  };
};

const OPEN_STATUSES = ['PREVIEWED', 'PENDING_APPROVAL', 'APPROVED'];
const SOON_MS = 2 * MS_HOUR;

/* The stamp has minute precision, so the deadline shown is at most a minute early, never late. */
export const save_deadline = (change, now) => {
  const c = change || {};
  if (!OPEN_STATUSES.includes(c.status)) return null;
  const p = c.preview;
  if (!p || p.status !== 'DONE') return null;
  const t = ist_stamp_ms(p.finished_at_ist);
  if (!Number.isFinite(t)) return null;
  const until = t + LIMITS.preview_max_age_hours * MS_HOUR;
  const left = until - now.getTime();
  const at = ist_parts_of(until);
  const needs_approval =
    c.status !== 'PREVIEWED' || codes_of(c.approval_reasons).length > 0 || !!c.approval;
  let warn = '';
  if (left > 0 && left < SOON_MS) {
    warn = needs_approval
      ? `This preview expires at ${at.clock} IST. After that, re-run it and get it approved again.`
      : `This preview expires at ${at.clock} IST. After that, re-run it.`;
  }
  return {
    expired: left <= 0,
    text: left > 0 ? `Save before ${at.clock} IST on ${day_label(at.day)}.` : '',
    warn
  };
};

export const today_warning = (change, now) => {
  const c = change || {};
  if (!OPEN_STATUSES.includes(c.status) || c.effective_date !== ist_day(now)) return '';
  if (codes_of(c.approval_reasons).includes('BACK_DATED')) return '';
  if (c.status === 'APPROVED')
    return 'Save it today: from tomorrow this change counts as back-dated and needs a new approval.';
  if (c.status === 'PENDING_APPROVAL')
    return 'Approve and save it today: from tomorrow this change counts as back-dated and needs a new approval.';
  return 'Save it today: from tomorrow this change counts as back-dated and needs approval.';
};

// The confirm dialogs for the terminal actions.
export const confirm_copy = (kind, asker_name = '') => {
  if (kind === 'discard')
    return {
      title: 'Discard this draft?',
      text: 'Nothing was sent for approval. It will not be saved and cannot be reopened.',
      keep: 'Keep it',
      go: 'Discard draft'
    };
  if (kind === 'withdraw')
    return {
      title: 'Withdraw this change?',
      text: 'It will not be saved and cannot be reopened. You will need to prepare it again.',
      keep: 'Keep it',
      go: 'Withdraw'
    };
  return {
    title: 'Reject this change?',
    text: `${asker_name || 'The person who asked'} will see your note. The change cannot be reopened.`,
    keep: 'Cancel',
    go: 'Reject'
  };
};

export const prepare_again_of = (change, list, cat = FALLBACK_CATALOGUE) => {
  const c = change || {};
  if (c.status !== 'REJECTED' && c.status !== 'WITHDRAWN') return null;
  const versions = Array.isArray(c.versions) ? c.versions : [];
  if (versions.length !== 1 || versions[0].kind !== 'RULE') return null;
  const after = (versions[0].after && versions[0].after.rule) || null;
  if (!after) return null;
  let version = null;
  ((list && list.stages) || []).forEach((s) =>
    s.groups.forEach((g) =>
      g.rules.forEach((r) => {
        if (r.rule_key === versions[0].rule_key && r.status === 'CURRENT') version = r;
      })
    )
  );
  if (!version || !version.rule) return null;
  const ids = new Set(editable_fields(version.rule, cat).map((f) => f.id));
  const values = Object.fromEntries(
    Object.entries(initial_values(editable_fields(after, cat))).filter(([id]) => ids.has(id))
  );
  let notice_mode = '';
  if (c.teacher_notice) notice_mode = 'notice';
  else if (c.no_notice_reason) notice_mode = 'none';
  return {
    version,
    prefill: {
      values,
      effective_date: c.effective_date || '',
      note: c.note || '',
      notice_mode,
      teacher_notice: c.teacher_notice || '',
      no_notice_reason: c.no_notice_reason || ''
    }
  };
};

// Approve, then save the approved preview, as one action for the approver; the save step runs only if
// the approval landed and nothing blocks it now.
export const save_after_approval = (approved, now) =>
  !!approved && approved.status === 'APPROVED' && apply_state(approved, now).enabled;

// ?change=<uuid> (the Slack link) opens that change once.
export const change_from_search = (search) => {
  const m =
    typeof search === 'string' ? search.match(/[?&]change=([0-9a-fA-F-]{36})(?:&|$)/) : null;
  return m ? m[1] : '';
};

// The history's "What changed" cell.
export const version_changed_text = (r) => {
  const x = r || {};
  if (x.is_seed) return 'First version';
  const fields = changed_fields_text(x.changed_fields || []);
  if (fields) return fields;
  return x.is_amendment ? 'Amended: see the note' : EM_DASH;
};
